import { getRepo, type AuditEntry } from '../../lib/repo';
import { useAsync } from '../../lib/hooks';
import { absoluteDate, relativeTime } from '../../lib/format';
import { Badge, Card, CardHeader, EmptyState, Spinner } from '../../components/ui';

/* ------------------------------------------------------------------ Audit -- */
export function AuditPage() {
  const { data, loading } = useAsync<AuditEntry[]>(() => getRepo().listAuditLog(), []);
  const rows = data ?? [];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold">Audit log</h1>
        <p className="mt-1 text-sm text-muted">
          Every privileged action, appended and never edited. Records actor, action, resource, outcome,
          a correlation id and a server timestamp. Secrets, tokens and payment credentials are never logged.
        </p>
      </header>

      <Card>
        <CardHeader title="Recent activity" description="Newest first." />
        {loading ? <Spinner label="Loading audit trail…" /> : null}
        {!loading && rows.length === 0 ? (
          <EmptyState title="No activity yet" description="Privileged actions will appear here as they happen." />
        ) : null}
        {rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted">
                  <th className="px-5 py-3 font-medium">When</th>
                  <th className="px-5 py-3 font-medium">Actor</th>
                  <th className="px-5 py-3 font-medium">Action</th>
                  <th className="px-5 py-3 font-medium">Resource</th>
                  <th className="px-5 py-3 font-medium">Outcome</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap px-5 py-3 text-muted">
                      {absoluteDate(r.at)}<span className="ml-1 text-xs">({relativeTime(r.at)})</span>
                    </td>
                    <td className="px-5 py-3">{r.actor}</td>
                    <td className="px-5 py-3 font-mono text-xs">{r.action}</td>
                    <td className="px-5 py-3">{r.resource}</td>
                    <td className="px-5 py-3">
                      <Badge tone={r.outcome === 'ok' ? 'published' : r.outcome === 'denied' ? 'in_review' : 'draft'}>{r.outcome}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------- Analytics -- */
const EVENTS: { name: string; description: string }[] = [
  { name: 'page_view', description: 'A reader opened any page.' },
  { name: 'article_view', description: 'A reader opened an article page (the canonical content unit).' },
  { name: 'article_engaged', description: 'The reader met a defined engagement rule on an article.' },
  { name: 'scroll_depth', description: 'The reader reached a defined scroll milestone.' },
  { name: 'related_article_click', description: 'A click on a “keep reading” story, attributed to the source article.' },
  { name: 'share_click', description: 'A share control was used. A click is not proof of a completed external share.' },
  { name: 'cta_impression', description: 'A call-to-action was rendered into the viewport.' },
  { name: 'cta_click', description: 'A call-to-action was clicked.' },
  { name: 'newsletter_signup', description: 'A newsletter subscription completed.' },
  { name: 'membership_checkout_started', description: 'A membership checkout flow was started.' },
  { name: 'membership_purchase', description: 'A purchase confirmed by a verified, server-side payment event.' },
];

export function AnalyticsPage() {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold">Analytics</h1>
        <p className="mt-1 text-sm text-muted">Definitions first, then collection, then charts.</p>
      </header>

      <EmptyState
        title="No analytics data yet"
        description="No events are being collected, so no figures are shown — inventing numbers here would be worse than showing none. Define the events below, collect them server-side (consent-aware, no unnecessary personal data), then build reports on top."
      />

      <Card>
        <CardHeader
          title="Event definitions"
          description="Agree the semantics before implementation. A share-button click is not a completed share; session duration is not retention."
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted">
                <th className="px-5 py-3 font-medium">Event</th>
                <th className="px-5 py-3 font-medium">Meaning</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {EVENTS.map((e) => (
                <tr key={e.name}>
                  <td className="whitespace-nowrap px-5 py-3 font-mono text-xs">{e.name}</td>
                  <td className="px-5 py-3 text-muted">{e.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------- Ads -- */
const AD_ENTITIES: { name: string; description: string }[] = [
  { name: 'advertisers', description: 'Accounts that may book inventory.' },
  { name: 'ad_slots', description: 'Named placements with a size and a daily price.' },
  { name: 'ad_bookings', description: 'A creative booked into a slot for a date range.' },
];

export function AdsPage() {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold">Advertising</h1>
        <p className="mt-1 text-sm text-muted">Paid placements, clearly labelled as such.</p>
      </header>

      <EmptyState
        title="Nothing configured yet"
        description="No ad slots, bookings or advertisers exist. Inventory and revenue will be reported here once placements are created — no numbers are shown until there is real data."
      />

      <Card>
        <CardHeader title="Planned entities" description="These tables already exist in the schema, empty." />
        <ul className="divide-y divide-border text-sm">
          {AD_ENTITIES.map((e) => (
            <li key={e.name} className="flex flex-col gap-1 px-5 py-3 sm:flex-row sm:items-baseline sm:gap-4">
              <span className="font-mono text-xs sm:w-40">{e.name}</span>
              <span className="text-muted">{e.description}</span>
            </li>
          ))}
        </ul>
      </Card>

      <p className="text-xs text-muted">
        Sponsored content must be visibly disclosed and kept separate from editorial reporting.
      </p>
    </div>
  );
}
